import type { TaxEstimate as TaxEstimateData } from '@eriko/shared-types';

export function TaxEstimateSkeleton() {
  return (
    <div
      className="relative bg-ink-deep rounded-[6px] px-8 py-7 flex flex-col animate-pulse"
      aria-hidden
    >
      <div className="flex items-center justify-between mb-5">
        <span className="h-2.5 w-20 bg-white/10 rounded-sm" />
        <span className="h-3 w-16 bg-white/10 rounded-sm" />
      </div>
      <div className="h-6 w-3/4 bg-white/10 rounded-sm mb-2" />
      <div className="h-6 w-1/2 bg-white/10 rounded-sm mb-6" />
      <div className="flex items-baseline gap-2 mb-auto">
        <span className="h-8 w-4 bg-white/10 rounded-sm" />
        <span className="h-14 w-40 bg-white/10 rounded-sm" />
        <span className="h-6 w-8 bg-white/10 rounded-sm" />
      </div>
      <div className="mt-8 pl-4 border-l border-gold flex flex-col gap-1.5">
        <span className="h-3 w-full bg-white/10 rounded-sm" />
        <span className="h-3 w-5/6 bg-white/10 rounded-sm" />
        <span className="h-3 w-2/3 bg-white/10 rounded-sm" />
      </div>
    </div>
  );
}

export function TaxEstimateCard({ data }: { data: TaxEstimateData }) {
  const [netProfitStr, dueDateStr] = data.noteHighlights;

  // Currency-aware big number — the org's home currency drives the symbol +
  // locale grouping (e.g. GBP → £ / en-GB), matching the server-formatted
  // noteHighlights. Falls back to USD when currency is absent (back-compat).
  const currency = data.currency ?? 'USD';
  const locale = currency === 'GBP' ? 'en-GB' : 'en-US';
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(data.amount);
  const match = formatted.match(/^([^\d]*)([\d,]+)(\.\d+)?$/);
  const prefix = match?.[1] ?? '$';
  const whole = match?.[2] ?? '0';
  const decimal = match?.[3] ?? '';

  return (
    <div className="relative bg-ink-deep text-surface-50 rounded-[6px] px-8 py-7 flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-[10px] uppercase tracking-[2px] text-white/50">
          Tax estimate
        </span>
        <span className="font-serif italic text-[12px] text-gold">
          {data.quarter} · {new Date().getFullYear()}
        </span>
      </div>

      <h3 className="font-serif text-[22px] leading-snug tracking-tight text-white/90 mb-6">
        Set aside for <span className="italic text-gold">{data.quarter}</span> estimated taxes
      </h3>

      <div className="flex items-baseline mb-auto">
        <span className="font-serif text-[22px] text-white/50 mr-1">{prefix}</span>
        <span className="font-serif text-[64px] leading-none tracking-tight text-white">
          {whole}
        </span>
        {decimal && (
          <span className="font-serif text-[22px] text-white/40">{decimal}</span>
        )}
      </div>

      <div className="mt-8 pl-4 border-l border-gold text-[12px] leading-relaxed font-serif italic text-white/70">
        Estimated on{' '}
        <strong className="not-italic font-semibold font-mono text-[11px] text-gold">
          {netProfitStr}
        </strong>{' '}
        this quarter, using current tax bands. Your next payment is due{' '}
        <strong className="not-italic font-semibold font-mono text-[11px] text-gold">
          {dueDateStr}
        </strong>{' '}
        — we'll remind you a week ahead.
      </div>
    </div>
  );
}
